import { inject, Injectable, InjectionToken, reflectComponentType, Type } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { IPageViewTelemetry } from '@microsoft/applicationinsights-web';
import { listenLoadRemoteException } from '@snelstart/feature-load-remote';
import { AccountClaim } from '@snelstart/util-access-token';
import { LoggerWriterService } from '@snelstart/util-logging';

export const AI_INSTRUMENTATION_KEY = new InjectionToken<string>('AI_INSTRUMENTATION_KEY');

@Injectable()
export class ApplicationInsightsMonitoringService extends LoggerWriterService {
    private _numericRange: [number, number] = [0, 9999];
    private _upperCaseRange: [number, number] = [65, 90];
    private appInsights: ApplicationInsights;

    private authService = inject(AuthService);
    private router = inject(Router);
    private instrumentationKey = inject(AI_INSTRUMENTATION_KEY);

    private authenticatedUserId?: string;
    private accountId?: string;

    private currentOperationId?: string;
    private currentOperationName?: string;

    constructor() {
        super();
        this.appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: this.instrumentationKey,
                enableAutoRouteTracking: false,
            },
        });
        this.appInsights.addTelemetryInitializer((envelope) => {
            if (envelope.ext) {
                envelope.ext['trace']['name'] = this.currentOperationName;
                envelope.ext['trace']['id'] = this.currentOperationId;
            }
        });
        this.appInsights.loadAppInsights();

        listenLoadRemoteException().subscribe((e) => this.logException(e.detail));

        this.router.events.subscribe({
            next: (event) => {
                if (event instanceof NavigationEnd) {
                    const { uri, componentSelector } = this.getCurrentRouteParameterizedPath();

                    this.currentOperationId = this.generate();
                    this.currentOperationName = uri;

                    const pageView = {
                        name: componentSelector,
                        uri: uri,
                        refUri: event.urlAfterRedirects || event.url,

                        properties: {
                            duration: 0,
                            authenticatedUserId: this.authenticatedUserId,
                            accountId: this.accountId,
                        },
                    } as IPageViewTelemetry;
                    this.appInsights.trackPageView(pageView);
                }
            },
        });
    }

    public getCurrentRouteParameterizedPath(): { uri: string; componentSelector: string } {
        const currentRoute = this.router.routerState.snapshot.root;
        return this.getParameterizedPathFromRouteSnapshot(currentRoute);
    }

    private getParameterizedPathFromRouteSnapshot(route: ActivatedRouteSnapshot): { uri: string; componentSelector: string } {
        const pathSegments: string[] = [];
        let currentRoute: ActivatedRouteSnapshot | null = route;
        let currentComponent: Type<unknown> | undefined;

        while (currentRoute) {
            if (currentRoute.component) {
                currentComponent = currentRoute.component;
            }
            if (currentRoute.routeConfig && currentRoute.routeConfig.path) {
                pathSegments.push(currentRoute.routeConfig.path);
            } else if (currentRoute.url && currentRoute.url.length > 0 && !currentRoute.routeConfig?.redirectTo) {
                if (currentRoute.url.length) {
                    // If there are url segments but no routeConfig path, use the segments as a fallback
                    pathSegments.push(...currentRoute.url.map((url) => url.path));
                }
            }

            currentRoute = currentRoute.firstChild;
        }

        let componentSelector = '';
        if (currentComponent) {
            componentSelector = reflectComponentType(currentComponent)?.selector ?? '';
        }

        return {
            uri: '/' + pathSegments.filter((segment) => segment).join('/'),
            componentSelector: componentSelector,
        };
    }

    public logEvent(name: string, properties?: Record<string, unknown>, eventId?: number) {
        if (eventId) {
            properties = properties ?? {};
            properties['eventId'] = eventId;
        }
        this.appInsights.trackEvent({ name: name }, properties);
    }

    public logException(exception: Error, incidentId?: string, parameters?: Record<string, unknown>) {
        incidentId = incidentId ?? this.generate();
        this.appInsights.trackException({ exception: exception, properties: { incidentId, ...parameters } });
    }

    public setUserContext(accountClaims: AccountClaim | undefined) {
        this.accountId = accountClaims?.accountCode;
        if (this.authenticatedUserId !== undefined) {
            return;
        }

        this.authService.user$.subscribe((user) => {
            if (user?.sub) {
                this.authenticatedUserId = user.sub;
                this.appInsights.setAuthenticatedUserContext(user.sub, accountClaims?.accountCode, true);
            }
        });
    }

    private generate(): string {
        return this.generateString() + this.generateNumber() + '-' + this.generateString() + this.generateNumber();
    }

    private generateString(): string {
        return String.fromCharCode(this.generateCharacterCode(), this.generateCharacterCode());
    }

    private generateCharacterCode(): number {
        return Math.floor(Math.random() * (this._upperCaseRange[1] - this._upperCaseRange[0])) + this._upperCaseRange[0];
    }

    private generateNumber(): string {
        const randomNumber = Math.floor(Math.random() * (this._numericRange[1] - this._numericRange[0])) + this._numericRange[0];
        const randomNumberString = randomNumber.toString();
        return ('0000' + randomNumberString).substring(randomNumberString.length);
    }
}
